/** @file This file implements a Signal class. */
import { EitherAsync, Right } from 'purify-ts';
import { Maybe } from '@codegrade/maybe';
import * as itertools from './itertools';
import { RegisterArray } from './register_array';
/**
 * Represents a signal system where callbacks (listeners) can be registered to respond to certain
 * events.
 *
 * Optionally supports "sticky" behavior, where callbacks registered after an event has fired are
 * immediately invoked with the last sent value.
 */
export class Signal {
    name;
    /** Internally stores the registered callbacks in an array. */
    arr = new RegisterArray();
    /** Stores synchronous callbacks that fire before async handlers, in the same tick as send. */
    syncArr = new RegisterArray();
    /** If set, registering on a signal that already fired will fire the signal directly. */
    sticky;
    /** If this signal is "sticky" the last send value is stored here. */
    cachedValue = Maybe.empty();
    /** If set this is a sequential signal, meaning only one may be in flight at the same time. */
    previous;
    /**
     * @param name The name of the new signal.
     * @param root0 Extra options for the signal.
     */
    constructor(name, { sticky = false, sequential = false, } = {}) {
        this.name = name;
        this.sticky = sticky;
        if (sequential) {
            this.previous = Promise.resolve();
        }
        else {
            this.previous = null;
        }
    }
    /**
     * Registers a callback to the signal.
     *
     * If the signal is sticky and has already fired, the callback is immediately invoked with the
     * last sent value.
     *
     * @param callback The callback to register.
     * @returns A function to unregister the callback.
     */
    register(callback) {
        this.cachedValue.ifJust(value => {
            callback(...value).catch(err => {
                // eslint-disable-next-line no-console
                console.error(new Error(`Listener ${callback.toString()} failed: ${err}`, { cause: err }));
            });
        });
        return this.arr.add(callback);
    }
    /**
     * Registers a synchronous callback that fires in the same tick as
     * {@link Signal.send}. Runs before any async handlers. Use this for work
     * that must complete before the microtask boundary, such as clearing Vuex
     * stores so Vue never renders stale state.
     *
     * @param callback The synchronous callback to register.
     * @returns A function to unregister the callback.
     */
    registerSync(callback) {
        this.cachedValue.ifJust(value => {
            callback(...value);
        });
        return this.syncArr.add(callback);
    }
    /**
     * Fire all synchronous listeners. Called at the start of every send, in the
     * same tick, before any async work.
     *
     * @param args The arguments to forward.
     */
    fireSyncListeners(...args) {
        for (const listener of this.syncArr) {
            try {
                listener(...args);
            }
            catch (err) {
                // eslint-disable-next-line no-console
                console.error(new Error(`Sync listener ${listener.toString()} failed`, { cause: err }));
            }
        }
    }
    /**
     * Send the actual signal, without any waiting.
     *
     * This should only be used internally when you are handing `this.previous` yourself.
     *
     * @param args The arguments to send.
     * @returns A promise with information about handler status.
     */
    async innerSend(...args) {
        if (this.sticky) {
            this.cachedValue = Maybe.of(args);
        }
        return await Promise.allSettled(itertools.map(this.arr, async (listener) => {
            try {
                await listener(...args);
            }
            catch (err) {
                // eslint-disable-next-line no-console
                console.error(new Error(`Listener ${listener.toString()} failed`, { cause: err }));
                throw err;
            }
        }));
    }
    /**
     * Same as `send` but provides you with information about failing handlers.
     *
     * @param args The arguments to send.
     * @returns A promise with information about handler status.
     */
    async sendWithErrors(...args) {
        // Fire synchronous listeners immediately, in the caller's tick,
        // before any microtask boundary. This guarantees that sync work
        // (like clearing Vuex stores) completes before Vue re-renders.
        this.fireSyncListeners(...args);
        if (this.previous != null) {
            const promise = this.previous.then(() => this.innerSend(...args));
            this.previous = promise.then(() => { });
            return await promise;
        }
        return await this.innerSend(...args);
    }
    /**
     * Send the given arguments to the listeners of this signal.
     *
     * @param args The arguments to send.
     * @returns An EitherAsync that does the sending, ingoring any errors from handlers.
     */
    send(...args) {
        return EitherAsync.fromPromise(async () => {
            await this.sendWithErrors(...args);
            return Right(undefined);
        });
    }
    /**
     * Send the given arguments to the listeners of this signal.
     *
     * @param args The arguments to send.
     * @returns A promise that never rejects.
     */
    async sendPromise(...args) {
        await this.sendWithErrors(...args);
    }
    /**
     * Lazily evaluates arguments and sends them to all registered listeners.
     *
     * Useful when the computation of arguments is expensive or asynchronous.
     *
     * @param getArgs A function that lazily evaluates and provides the arguments.
     * @returns An `EitherAsync` wrapper for asynchronous handling of listeners and errors.
     */
    sendLazy(getArgs) {
        if (this.arr.size === 0 && this.syncArr.size === 0) {
            return EitherAsync(async () => { });
        }
        return getArgs().chain(args => this.send(...args));
    }
}
